import { Locale } from "@/Common/Models/Localization/Locale";
import { ObstetricalCounter } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalCountersComponent";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { BritishColumbiaObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BritishColumbia/BritishColumbiaObstetricalFileForm";
import { CareProviderRole, CareProviderRoleHelper } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/CareProviderRole";
import { BritishColumbiaObstetricalVisit } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part2/BritishColumbiaObstetricalVisit";
import { IObstetricalFilePageProps, ObstetricalFilePage, ObstetricalFilePageState } from "../ObstetricalFilePage";
import { BaseBritishColumbiaObstetricalFileSubSectionProps } from "./Subcomponent/BaseBritishColumbiaObstetricalFileSubSection";

abstract class BritishColumbiaObstetricalFilePageState extends ObstetricalFilePageState<BritishColumbiaObstetricalFileForm> {
    public currentVisitId: number;
}

abstract class BritishColumbiaObstetricalFilePage<TState extends BritishColumbiaObstetricalFilePageState> extends ObstetricalFilePage<BritishColumbiaObstetricalFileForm, TState> {
    protected vitalSignControlRenderer?: ObstetricalFileControlRenderer = null;
    protected get locale(): Locale { return Locale.en; }

    public componentWillMount(): void {
        super.componentWillMount();
        cacheStore.RequestApplicationStringAsync(Locale.en).then(() => this.forceUpdate());
        this.setVitalSignControlRenderer(this.props.readonly);
    }

    public componentWillReceiveProps(nextProps: IObstetricalFilePageProps<BritishColumbiaObstetricalFileForm>): void {
        super.componentWillReceiveProps(nextProps);
        if (this.props.readonly !== nextProps.readonly)
            this.setVitalSignControlRenderer(nextProps.readonly);
    }

    protected setVitalSignControlRenderer(readonly: boolean): void {
        this.vitalSignControlRenderer = new ObstetricalFileControlRenderer({
            readonly: readonly,
            onBlur: () => this.onVitalSignUpdated(),
            locale: this.locale
        });
    }

    protected onVitalSignUpdated(): void {
        throw new Error("The vital sign control renderer callback has not been implemented for the current page.");
    }

    protected getSubcomponentBaseProps(): BaseBritishColumbiaObstetricalFileSubSectionProps {
        return {
            patient: this.props.patient,
            contributionId: this.props.contributionId,
            readonly: this.props.readonly,
            previousObstetricalFile: this.props.previousObstetricalFile,
            consultationDateAtStartOfObstetricalFile: this.props.consultationDateAtStartOfObstetricalFile,
            obstetricalFileMedicalSummary: this.props.obstetricalFileMedicalSummary,
            handleFormUpdate: () => this.handleFormUpdate(),
            form: this.state.form,
            clickableTextId: this.state.currentClickableTextId,
            onClickableTextChange: (id: string) => this.mutateState(s => s.currentClickableTextId = id),
            mutateModelState: (mutation, then, shouldAutofillSignOffs) => this.mutateModelState(s => mutation(s.form), then, shouldAutofillSignOffs),
            mutateModelStateWithFormUpdate: (mutation, then, shouldAutofillSignOffs) => this.mutateModelStateWithFormUpdate(s => mutation(s.form), then, shouldAutofillSignOffs),
            renderObstetricalCounters: (colClassName: string) => this.renderObstetricalCounters(
                colClassName,
                false, // render as field
                false, // render headers
                [
                    ObstetricalCounter.Gravida,
                    ObstetricalCounter.Term,
                    ObstetricalCounter.Premature,
                    ObstetricalCounter.AbortionInduced,
                    ObstetricalCounter.AbortionSpontaneous,
                    ObstetricalCounter.Live
                ], // include fields
                true, // show alternate abortion counter
                true, // long labels
                true, // large first letter labels
                {
                    side: {
                        [ObstetricalCounter.Live]: "LivingLabel",
                        [ObstetricalCounter.Premature]: "ObstetricalFile_Preterm"
                    }
                },
                Locale.en
            ),
            getClickableTextOptions: (id, context, leftAlign) => this.getClickableTextOptions(id, context, leftAlign),
            fetchLabObservations: this.props.fetchLabObservations
        };
    }

    protected mutateModelStateWithFormUpdate(mutation: (state: TState) => void, then?: () => void, shouldAutofillSignOffs: boolean = true): void {
        this.mutateState(
            s => {
                mutation(s);
                if (shouldAutofillSignOffs)
                    this.autofillSignOffsSection(s.form);
            },
            () => {
                then?.();
                this.handleFormUpdate();
            }
        );
    }

    protected mutateModelState(mutation: (state: TState) => void, then?: () => void, shouldAutofillSignOffs: boolean = true): void {
        this.mutateState(
            s => {
                mutation(s);
                if (shouldAutofillSignOffs)
                    this.autofillSignOffsSection(s.form);
            },
            then
        );
    }

    protected initializePrenatalVisit(state: BritishColumbiaObstetricalFileForm, props: IObstetricalFilePageProps<BritishColumbiaObstetricalFileForm>): void {
        state.Section17 = [
            new BritishColumbiaObstetricalVisit({
                ContributionId: props.contributionId,
                Date: props.encounter.ConsultationDate
            })
        ];
    }

    private autofillSignOffsSection(state: BritishColumbiaObstetricalFileForm): void {
        let firstEmptyRow = state.Section18?.firstOrDefault(p => !p.CareProviderName);
        if (!firstEmptyRow)
            return;

        const careProvider = sessionStore.getCurrentEmployee();
        const careProviderRole = CareProviderRoleHelper.getCareProviderRoleByEmployee(careProvider);

        if (
            careProvider && careProviderRole !== CareProviderRole.Other
            && !state.Section18?.any(p => p.CareProviderName.contains(careProvider.Name.trim()))
            && !state.Metadata.SignificantContributorIds.contains(careProvider.Model.Contact_id)
        ) {
            state.Metadata.SignificantContributorIds.push(careProvider.Model.Contact_id);

            state.Section18.mutate(
                p => {
                    p.CareProviderName = careProvider.Name;
                    p.CareProviderRole = careProviderRole;
                },
                p => p.getId() === firstEmptyRow.getId()
            );
        }
    }
}

export { BritishColumbiaObstetricalFilePage, BritishColumbiaObstetricalFilePageState };
